import React, { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const CHART_COLORS = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#ea580c",
  "#0891b2",
  "#be185d",
  "#ca8a04",
  "#4f46e5",
  "#059669",
  "#c2410c",
  "#7c3aed",
  "#0d9488",
  "#db2777",
  "#65a30d",
];

const formatAmount = (value) =>
  typeof value === "number"
    ? value.toLocaleString("ru-RU", { maximumFractionDigits: 0 })
    : value;

const ChartTooltip = ({ active, payload, label, restaurantNames }) => {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="z-report-chart-tooltip">
      <p className="z-report-chart-tooltip-date">{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          className="z-report-chart-tooltip-row"
          style={{ color: entry.color }}
        >
          <span>{restaurantNames[entry.dataKey]}</span>
          <strong>{formatAmount(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const ZReportChart = ({ report }) => {
  const restaurants = report?.restaurants || [];

  const [visibleIds, setVisibleIds] = useState({});

  useEffect(() => {
    if (!report?.restaurants?.length) {
      setVisibleIds({});
      return;
    }
    const next = {};
    report.restaurants.forEach((restaurant) => {
      next[restaurant.restaurant_id] = true;
    });
    setVisibleIds(next);
  }, [report]);

  const chartData = useMemo(() => {
    if (!report?.dates?.length) {
      return [];
    }
    return report.dates.map((dateKey, index) => {
      const row = { date: report.date_labels[index] };
      restaurants.forEach((restaurant) => {
        const day = restaurant.days?.find((item) => item.date === dateKey);
        const cash = day?.cash || 0;
        const ecash = day?.ecash || 0;
        row[restaurant.restaurant_id] = cash + ecash;
      });
      return row;
    });
  }, [report, restaurants]);

  const restaurantNames = useMemo(() => {
    const map = {};
    restaurants.forEach((restaurant) => {
      map[restaurant.restaurant_id] = restaurant.restaurant_name;
    });
    return map;
  }, [restaurants]);

  const visibleRestaurants = restaurants.filter(
    (restaurant) => visibleIds[restaurant.restaurant_id],
  );

  const toggleRestaurant = (restaurantId) => {
    setVisibleIds((prev) => ({
      ...prev,
      [restaurantId]: !prev[restaurantId],
    }));
  };

  const setAllVisible = (value) => {
    const next = {};
    restaurants.forEach((restaurant) => {
      next[restaurant.restaurant_id] = value;
    });
    setVisibleIds(next);
  };

  if (!restaurants.length) {
    return null;
  }

  return (
    <div className="z-report-chart-section">
      <div className="z-report-chart-header">
        <h4 className="z-report-chart-title">График по сумме (нал + безнал)</h4>
        <div className="z-report-chart-actions">
          <button
            type="button"
            className="z-report-ghost-btn"
            onClick={() => setAllVisible(true)}
          >
            Включить все
          </button>
          <button
            type="button"
            className="z-report-ghost-btn"
            onClick={() => setAllVisible(false)}
          >
            Выключить все
          </button>
        </div>
      </div>

      <div className="z-report-chart-filters">
        {restaurants.map((restaurant, index) => {
          const color = CHART_COLORS[index % CHART_COLORS.length];
          const isVisible = visibleIds[restaurant.restaurant_id];
          return (
            <label
              key={restaurant.restaurant_id}
              className={`z-report-chart-filter ${isVisible ? "is-active" : ""}`}
              style={{ "--filter-color": color }}
            >
              <input
                type="checkbox"
                checked={Boolean(isVisible)}
                onChange={() => toggleRestaurant(restaurant.restaurant_id)}
              />
              <span className="z-report-chart-filter-dot" />
              <span className="z-report-chart-filter-name">
                {restaurant.restaurant_name}
              </span>
            </label>
          );
        })}
      </div>

      {visibleRestaurants.length === 0 ? (
        <p className="z-report-chart-empty">Выберите хотя бы один ресторан для графика</p>
      ) : (
        <div className="z-report-chart-wrap">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dbe4f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={formatAmount}
                width={72}
              />
              <Tooltip
                content={<ChartTooltip restaurantNames={restaurantNames} />}
              />
              {visibleRestaurants.map((restaurant) => {
                const colorIndex = restaurants.findIndex(
                  (item) => item.restaurant_id === restaurant.restaurant_id,
                );
                return (
                  <Line
                    key={restaurant.restaurant_id}
                    type="monotone"
                    dataKey={restaurant.restaurant_id}
                    name={String(restaurant.restaurant_id)}
                    stroke={CHART_COLORS[colorIndex % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    connectNulls
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default ZReportChart;
